import { useContext, useEffect, useState } from "react";

import { isEmpty } from "@medplum/core";
import { Questionnaire } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Alert, AlertProps } from "@mantine/core";

import { IconExclamationCircle } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import { ProfileContext } from "@/contexts/profile";
import { fetchRemovedIntakeItemLabels } from "@/settings/services/intake-dependencies";
import { IntakeDependentFeature } from "@/settings/types/client-intake";
import { getActiveOrganizationId } from "@/utils/medplum";

/** Tells providers which intake content a feature depends on has been removed from the intake. */
export default function IntakeContentRemovedAlert({
  feature,
  clientId,
  clientIntake,
  ...props
}: IntakeContentRemovedAlertProps) {
  const medplum = useMedplum();
  const { isPatient } = useContext(ProfileContext);
  const [removedItemLabels, setRemovedItemLabels] = useState<string[]>([]);
  const [isClosed, setIsClosed] = useState(false);

  useEffect(() => {
    setRemovedItemLabels([]);
    if (isPatient) return;

    let isCancelled = false;

    fetchRemovedIntakeItemLabels(medplum, {
      feature,
      practiceId: getActiveOrganizationId(),
      clientId,
      clientIntake,
    })
      .then((labels) => {
        if (isCancelled) return;
        setRemovedItemLabels(labels);
      })
      .catch(() => {
        if (isCancelled) return;
        setRemovedItemLabels([]);
      });

    return () => {
      isCancelled = true;
    };
  }, [medplum, isPatient, feature, clientId, clientIntake]);

  if (isPatient || isEmpty(removedItemLabels)) return null;

  return (
    <Alert
      radius="sm"
      color="yellow"
      withCloseButton
      hidden={isClosed}
      closeButtonLabel="Close"
      onClose={() => setIsClosed(true)}
      title="Intake information removed"
      data-testid="intake-content-removed-alert"
      styles={{ title: { fontSize: "var(--mantine-font-size-md)" } }}
      icon={<IconExclamationCircle size={16} color="var(--mantine-color-orange-8)" />}
      {...props}
    >
      <TextWrapper mt="xs">
        This client&apos;s intake form didn&apos;t include {removedItemLabels.join(", ")}, because
        it was removed from the intake in Settings → Forms → Client Intake. The client couldn&apos;t
        enter this information, so {feature} may be missing it or not work as expected. To collect
        it from clients going forward, turn it back on in Client Intake settings.
      </TextWrapper>
    </Alert>
  );
}

interface IntakeContentRemovedAlertProps extends AlertProps {
  feature: IntakeDependentFeature;
  /** The client the screen shows. */
  clientId: string;
  /**
   * The version the client's intake response was answered on, when the screen already loaded it.
   * Leave it out when the client has no intake response.
   */
  clientIntake?: Questionnaire;
}
